import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  aplicaCoresOneShell,
  getConfigAmbiente,
  limparSessao
} from '../../services/authentication/auth';
import { limparDocumento, validaDocumentoPorTipo } from '../Utils/documentoAlfanumerico';
import { validaForcaSenha } from '../Utils/validarForcaSenha';
import { resetPassword } from '../../api/authentication';
import Strip from '../Utils/strip';

const useResetPasswordHook = () => {
  aplicaCoresOneShell();
  const { token } = useParams();
  const navigate = useNavigate();
  const [valuesPassword, setValuesPassword] = useState({ password: '', showPassword: false });
  const [valuesRepeatPassword, setRepeatPassword] = useState({
    repeatPassword: '',
    showRepeatPassword: false
  });
  const [textoForcaSenha, setTextoForcaSenha] = useState({
    msgForcaSenha: '',
    corForcaSenha: '',
    corProgress: ''
  });
  const [valueCPF, setValueCPF] = useState('');
  const [progress, setProgress] = useState(0);
  const [senhaIguais, setSenhaIguais] = useState(false);
  const [messageLocal, setMessageLocal] = useState('');
  const [isPending, setPending] = useState(false);
  const [message, setMessage] = useState('');
  const [mostrarErro, setMostraErro] = useState('');
  const configAmbiente = getConfigAmbiente();
  const imgLogoLogin =
    configAmbiente && configAmbiente.imagens && configAmbiente.imagens.LOGO
      ? configAmbiente.imagens.LOGO
      : '';
  const imgBackground =
    configAmbiente && configAmbiente.imagens && configAmbiente.imagens.LOGIN_BACKGROUND
      ? configAmbiente.imagens.LOGIN_BACKGROUND
      : '';
  const tipoAmbiente = window.location.protocol === 'https:' ? 'PRODUÇÃO' : 'TESTE';

  const resetPasswordHandler = (values, form) => {
    setPending(true);
    if (senhaIguais) {
      form.setSubmitting(false);
      return false;
    }

    if (progress < 50) {
      setMessage('Força de senha muito fraca');
      form.setSubmitting(false);
      return false;
    }

    const entity = {
      documento: Strip(values.cpf),
      senha: btoa(unescape(encodeURIComponent(values.password))),
      senha_confirmacao: btoa(unescape(encodeURIComponent(values.repeatPassword)))
    };

    resetPassword(entity, token)
      .then(res => {
        const { mensagem } = res.data;
        setMessage(mensagem);
      })
      .catch(err => {
        setMostraErro(err.response);
      })
      .finally(() => {
        form.setSubmitting(false);
        setPending(false);
      });
  };

  const validaSenhaDemaisCampos = (senha, cpf) => {
    if (senha === '') return false;
    if (Strip(senha) === Strip(cpf) || senha === cpf || senha === Strip(cpf)) {
      return false;
    }
    return true;
  };

  const verificaValidaSenha = password => {
    const forcaSenha = validaForcaSenha(password);
    let textoSenha = '';
    let corSenha = '';
    let corBarra = '';

    if (forcaSenha === 100) {
      textoSenha = 'Forte';
      corSenha = 'texto-success texto-semi-bold';
      corBarra = 'success';
    } else if (forcaSenha === 50) {
      textoSenha = 'Mediana';
      corSenha = 'texto-warning texto-semi-bold';
      corBarra = 'warning';
    } else if (forcaSenha > 0) {
      textoSenha = 'Fraca';
      corSenha = 'texto-danger texto-semi-bold';
      corBarra = 'error';
    }

    if (!validaSenhaDemaisCampos(password, valueCPF)) {
      textoSenha = 'Fraca';
      corSenha = 'texto-danger texto-semi-bold';
      corBarra = 'error';
      setProgress(5);
    } else {
      setProgress(forcaSenha);
    }

    setTextoForcaSenha({ msgForcaSenha: textoSenha, corForcaSenha: corSenha, corProgress: corBarra });
  };

  const validaSenhasIguais = (passConfirm, pass) => {
    const diferentes = passConfirm !== '' && passConfirm !== pass;
    setSenhaIguais(diferentes);
  };

  const handleChangePassword = (event, tipo) => {
    if (tipo === 'repeatPassword') {
      const val = event.target.value;
      setRepeatPassword(prev => ({ ...prev, repeatPassword: val }));
      setMessageLocal('');
      validaSenhasIguais(val, valuesPassword.password);
    } else if (tipo === 'password') {
      const val = event.target.value;
      setValuesPassword(prev => ({ ...prev, password: val }));
      setMessageLocal('');
      verificaValidaSenha(val);
      validaSenhasIguais(valuesRepeatPassword.repeatPassword, val);
    }
  };

  const handleClickShowPassword = tipo => {
    if (tipo === 'password') {
      setValuesPassword(prev => ({ ...prev, showPassword: !prev.showPassword }));
    } else {
      setRepeatPassword(prev => ({ ...prev, showRepeatPassword: !prev.showRepeatPassword }));
    }
  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  useEffect(() => {
    limparSessao();
  }, []);

  const validateInputs = useCallback(values => {
    const errors = {};
    const strippedCpf = limparDocumento(values.cpf);

    if (!values.cpf.trim()) {
      errors.cpf = 'Informe o CPF';
    } else if (!validaDocumentoPorTipo(strippedCpf, 'PF')) {
      errors.cpf = 'CPF inválido';
    }

    if (!values.password) {
      errors.password = 'Informe a senha';
    } else if (
      !validaSenhaDemaisCampos(values.password, values.cpf) ||
      validaForcaSenha(values.password) < 50
    ) {
      errors.password = 'Senha muito fraca';
    }

    if (!values.repeatPassword) {
      errors.repeatPassword = 'Informe a senha de confirmação';
    } else if (values.repeatPassword !== values.password) {
      errors.repeatPassword = 'As senhas precisam ser iguais';
    }

    return errors;
  }, []);

  return {
    navigate,
    isPending,
    message,
    mostrarErro,
    textoForcaSenha,
    setValueCPF,
    valuesPassword,
    valuesRepeatPassword,
    messageLocal,
    imgLogoLogin,
    imgBackground,
    tipoAmbiente,
    setMessageLocal,
    setValuesPassword,
    setRepeatPassword,
    resetPasswordHandler,
    handleChangePassword,
    handleClickShowPassword,
    handleMouseDownPassword,
    validaSenhaDemaisCampos,
    verificaValidaSenha,
    validateInputs,
    progress
    // value
  };
};

export default useResetPasswordHook;
